
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Home() {
  const { t } = useTranslation();
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 45,
    seconds: 26
  });

  const [selectedModel, setSelectedModel] = useState('model1');
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('action');
  const [openFaq, setOpenFaq] = useState(0);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const [showTechnicalPopup, setShowTechnicalPopup] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const defaultProducts = [
    {
      id: '1',
      title: 'Baterijski Model',
      price: '7990',
      currency: 'RSD',
      features: [
        'Baterija traje do 6 meseci',
        'Mogućnost solarnog punjenja',
        'Daljinskim upravljačem',
        'Vodootporna konstrukcija',
        'Lako za instalaciju'
      ]
    },
    {
      id: '2',
      title: 'Žični Model',
      price: '8490',
      currency: 'RSD',
      features: [
        'Direktno napajanje 220V',
        'Nikad se ne prazni',
        'Daljinskim upravljačem',
        'Maksimalna pouzdanost',
        'Profesionalna instalacija'
      ]
    }
  ];

  /* -------------------------------------------------------------------------- */
  /* Timer – counts down every second                                           */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  /* -------------------------------------------------------------------------- */
  /* Fetch products from Supabase function                                       */
  /* -------------------------------------------------------------------------- */
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(
        'https://iaqygofrzbypqawbahnz.supabase.co/functions/v1/get-related-products',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({})
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Fetched products:', data);
        setProducts(data.products || []);
      } else {
        console.error('Failed to fetch products:', response.status);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? -1 : index);
  };

  const openContactPopup = () => {
    setShowContactPopup(true);
  };

  const openTechnicalPopup = () => {
    setShowTechnicalPopup(true);
  };

  const openBulkForm = () => {
    setShowBulkForm(true);
  };

  const closePopups = () => {
    setShowContactPopup(false);
    setShowTechnicalPopup(false);
    setShowBulkForm(false);
  };

  const getSelectedProduct = () => {
    if (products.length >= 2) {
      return selectedModel === 'model1' ? products[0] : products[1];
    }
    return null;
  };

  const selectedProduct = getSelectedProduct();
  const productPrice = selectedProduct
    ? Math.round(parseFloat(selectedProduct.priceRange?.minVariantPrice?.amount || (selectedModel === 'model1' ? '7990' : '8490')))
    : (selectedModel === 'model1' ? 7990 : 8490);

  const selectModel = (model: 'battery' | 'wired') => {
    setSelectedModel(model);

    // dataLayer push - korisnik izabrao model (pre slanja forme)
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'select_model',
        content_name: model === 'battery' ? 'Baterijski model' : 'Žični model',
        value: model === 'battery' ? 7990 : 8490,
        currency: 'RSD'
      });
    }
  };

  const trackPhoneClick = () => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'contact_click',
        method: 'phone'
      });
    }
  };

  const scrollToCheckout = () => {
    // Google tag - Add to Cart event
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'add_to_cart', {
        currency: 'RSD',
        value: selectedModel === 'model1' ? 7990 : (selectedModel === 'battery' ? 7990 : 8490),
        items: [{
          item_id: selectedModel === 'model1' ? 'battery_model' : (selectedModel === 'battery' ? 'battery_model' : 'wired_model'),
          item_name: selectedModel === 'model1' ? 'Baterijski Model' : (selectedModel === 'battery' ? 'Baterijski Model' : 'Žični Model'),
          category: 'Parking Barrier',
          quantity: 1,
          price: selectedModel === 'model1' ? 7990 : (selectedModel === 'battery' ? 7990 : 8490)
        }]
      });
    }

    const checkoutSection = document.querySelector('#checkout-section');
    if (checkoutSection) {
      checkoutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formData = new FormData(e.currentTarget);

      const response = await fetch('send-order.php', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const unitPrice = selectedModel === 'battery' ? 7990 : 8490;
        const totalValue = unitPrice * quantity;

        // Google tag - Purchase/Success event
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'purchase', {
            transaction_id: Date.now().toString(),
            currency: 'RSD',
            value: totalValue + 390,
            items: [{
              item_id: selectedModel === 'battery' ? 'battery_model' : 'wired_model',
              item_name: selectedModel === 'battery' ? 'Baterijski Model' : 'Žični Model',
              category: 'Parking Barrier',
              quantity: quantity,
              price: unitPrice
            }]
          });
        }

        // dataLayer push - forma uspešno poslata (potvrđeno od servera)
        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'lead_form_submit',
            value: totalValue,
            currency: 'RSD',
            content_name: selectedModel === 'battery' ? 'Baterijski model' : 'Žični model',
            quantity: quantity
          });
        }

        // Meta Pixel - Lead event (direktno, ne zavisi od GTM-a)
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead', {
            value: totalValue,
            currency: 'RSD',
            content_name: selectedModel === 'battery' ? 'Baterijski model' : 'Žični model',
            quantity: quantity
          });
        }

        setShowSuccess(true);
        e.currentTarget.reset();
        setQuantity(1);
        setSelectedModel('battery');
        
        // Hide success message after 5 seconds
        setTimeout(() => {
          setShowSuccess(false);
        }, 5000);
      } else {
        console.error('Form submission failed, falling back to native submit');
        e.currentTarget.submit();
      }
    } catch (error) {
      console.error('Error submitting form, falling back to native submit:', error);
      e.currentTarget.submit();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Kontakt forma - šalje se na send-order.php, isti obrazac kao glavna forma
  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    try {
      const formData = new FormData(form);
      const response = await fetch('/send-order.php', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        if (typeof window !== 'undefined') {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'lead_form_submit',
            content_name: 'Kontakt forma'
          });
        }
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead', { content_name: 'Kontakt forma' });
        }
        form.reset();
        closePopups();
      } else {
        console.error('Contact form submission failed, falling back to native submit');
        form.submit();
      }
    } catch (error) {
      console.error('Error submitting contact form, falling back to native submit:', error);
      form.submit();
    }
  };

  // Tehnička podrška - action="mailto:..." ne može preko fetch-a, puštamo native slanje
  // i pucamo Lead event odmah na submit (stranica se ne gasi kod mailto: akcije)
  const handleTechnicalSubmit = () => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'lead_form_submit',
        content_name: 'Tehnička podrška'
      });
    }
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Lead', { content_name: 'Tehnička podrška' });
    }
    // ne pozivamo preventDefault - mailto: akcija ide svojim tokom kao i do sada
  };

  // Bulk porudžbina - takođe mailto:, isti princip kao tehnička podrška
  const handleBulkSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget);
    const barrierType = formData.get('barrier_type')?.toString() || '';
    const quantityNeeded = Number(formData.get('quantity_needed')) || undefined;

    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'lead_form_submit',
        content_name: 'Bulk porudžbina',
        barrier_type: barrierType,
        quantity: quantityNeeded
      });
    }
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', 'Lead', {
        content_name: 'Bulk porudžbina',
        quantity: quantityNeeded
      });
    }
    // ne pozivamo preventDefault - mailto: akcija ide svojim tokom kao i do sada
  };

  /* -------------------------------------------------------------------------- */
  /* Render                                                                    */
  /* -------------------------------------------------------------------------- */
  return (
    <div className="min-h-screen bg-white">
    <div className="bg-black text-white py-3 px-6 flex flex-col md:flex-row items-center justify-center gap-4 border-b border-white/10">

  <div className="flex items-center gap-2 text-lg md:text-xl font-bold">
    <span>📞</span>
    <a
      href="tel:+381612929053"
      onClick={trackPhoneClick}
      className="hover:text-yellow-400 transition"
    >
      061 29 29 053
    </a>
  </div>

</div>
      {/* --------------------------- Contact Popup --------------------------- */}
      {showContactPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <button
              onClick={closePopups}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Kontaktirajte nas
            </h3>

            <form
              id="contact-form"
              action="/send-order.php"
              method="POST"
              onSubmit={handleContactSubmit}
              className="space-y-4"
            >
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Ime i prezime *
                </label>
                <input
                  type="text"
                  name="full_name"
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Vaše ime i prezime"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="vas@email.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Telefon
                </label>
                <input
                  type="tel"
                  name="phone"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+381 60 123 4567"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Poruka *
                </label>
                <textarea
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Kako možemo da vam pomognemo?"
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Pošaljite poruku
              </button>
            </form>
          </div>
        </div>
      )}

      {/* --------------------------- Technical Support Popup --------------------------- */}
      {showTechnicalPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <button
              onClick={closePopups}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Tehnička podrška
            </h3>

            <div className="space-y-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <i className="ri-phone-line text-blue-600 text-xl mr-3"></i>
                  <h4 className="font-semibold text-gray-900">Pozovite nas</h4>
                </div>
                <p className="text-gray-700 mb-2">
                  Dostupni smo 24/7 za hitnu podršku
                </p>
                <a href="tel:+381612929053" onClick={trackPhoneClick} className="text-blue-600 font-bold text-lg">
                  061 29 29 053
                </a>
              </div>

              <form
                id="technical-form"
                data-readdy-form
                action="mailto:prodaja@parkingbarijera.rs"
                method="POST"
                onSubmit={handleTechnicalSubmit}
                className="space-y-4"
              >
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Ime i prezime *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Vaše ime i prezime"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="vas@email.com"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Tehnički problem *
                  </label>
                  <textarea
                    name="technical_issue"
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Opišite tehnički problem koji imate..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Pošaljite zahtev
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* --------------------------- Bulk Order Popup --------------------------- */}
      {showBulkForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
            <button
              onClick={closePopups}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            >
              <i className="ri-close-line text-gray-600"></i>
            </button>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Veće količine - Specijalne cene
            </h3>

            <form
              id="bulk-order-form"
              data-readdy-form
              action="mailto:prodaja@parkingbarijera.rs"
              method="POST"
              onSubmit={handleBulkSubmit}
              className="space-y-4"
            >
              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Ime i prezime *
                </label>
                <input
                  type="text"
                  name="full_name"
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Vaše ime i prezime"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="vas@email.com"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Telefon *
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="+381 60 123 4567"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Potrebna količina *
                </label>
                <input
                  type="number"
                  name="quantity_needed"
                  required
                  min="10"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Koliko barijera vam je potrebno?"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Tip barijere *
                </label>
                <select
                  name="barrier_type"
                  required
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 pr-8"
                >
                  <option value="">Izaberite tip</option>
                  <option value="baterijski">Baterijski model</option>
                  <option value="zicni">Žični model</option>
                  <option value="kombinovano">Kombinovano (oba tipa)</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">
                  Dodatne informacije
                </label>
                <textarea
                  name="additional_info"
                  rows={4}
                  maxLength={500}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Opišite vašu potrebu, lokaciju, vremenski okvir..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-green-700 transition-colors"
              >
                Pošaljite zahtev za cenu
              </button>
            </form>
          </div>
        </div>
      )}

      {/* --------------------------- Header --------------------------- */}
      <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                <i className="ri-parking-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">ParkGuard</span>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#demo" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                {t('header.product')}
              </a>
              <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                {t('header.features')}
              </a>
              <a href="#testimonials" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                {t('header.reviews')}
              </a>
              <a href="#faq" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                {t('header.faq')}
              </a>
              <a href="/blog" className="text-gray-600 hover:text-blue-600 transition-colors font-medium">
                Blog
              </a>
            </nav>
            <div className="hidden md:flex items-center space-x-4">
              <button
                onClick={scrollToCheckout}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap cursor-pointer"
              >
                {t('header.buyNow')}
              </button>
            </div>
            <button className="md:hidden w-10 h-10 flex items-center justify-center">
              <i className="ri-menu-line text-xl text-gray-600"></i>
            </button>
          </div>
        </div>
      </header>

      {/* --------------------------- Hero Section --------------------------- */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
          <div className="absolute inset-0 bg-cover opacity-20"></div>
          <div className="absolute top-10 sm:top-20 left-5 sm:left-10 w-32 sm:w-72 h-32 sm:h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 sm:bottom-20 right-5 sm:right-10 w-48 sm:w-96 h-48 sm:h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-full blur-3xl animate-spin-slow"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* ------ Text side ------ */}
            <div className="text-white space-y-6 sm:space-y-8 animate-fade-in-up order-2 lg:order-1">
              <div className="space-y-4 sm:space-y-6">
                <div className="inline-flex items-center bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-semibold shadow-lg backdrop-blur-sm border border-white/10">
                  <div className="w-2 h-2 bg-white rounded-full mr-2 sm:mr-3 animate-pulse"></div>
                  <span className="text-center">{t('hero.flashSale')}</span>
                </div>

                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent">
                  {t('hero.title')} <br />
                  <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                    {t('hero.titleHighlight')}
                  </span>
                </h1>

                <p className="text-base sm:text-lg lg:text-xl text-gray-300 leading-relaxed max-w-lg">
                  Revolucionarna pametna parking barijera sa daljinskim upravljačem. Pridružite se 10.000+ vlasnika nekretnina koji su zauvek zaustavili neovlašćeno parkiranje.
                </p>
              </div>

              {/* ------ Countdown ------ */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-2xl blur-xl"></div>
                <div className="relative bg-black/40 backdrop-blur-xl border border-white/10 rounded-2xl p-4 sm:p-6">
                  <div className="flex items-center mb-3 sm:mb-4">
                    <div className="w-2 sm:w-3 h-2 sm:h-3 bg-yellow-400 rounded-full mr-2 sm:mr-3 animate-pulse"></div>
                    <p className="text-yellow-400 font-semibold text-sm sm:text-base">{t('hero.flashSaleEnds')}</p>
                  </div>
                  <div className="flex space-x-2 sm:space-x-4">
                    {/* Hours */}
                    <div className="text-center flex-1">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-white/5 rounded-xl blur-sm"></div>
                        <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 text-white text-lg sm:text-2xl lg:text-3xl font-bold px-2 sm:px-4 py-2 sm:py-3 rounded-xl">
                          {timeLeft.hours}
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm mt-1 sm:mt-2 text-gray-300">{t('hero.hours')}</p>
                    </div>
                    {/* Minutes */}
                    <div className="text-center flex-1">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-white/5 rounded-xl blur-sm"></div>
                        <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 text-white text-lg sm:text-2xl lg:text-3xl font-bold px-2 sm:px-4 py-2 sm:py-3 rounded-xl">
                          {timeLeft.minutes}
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm mt-1 sm:mt-2 text-gray-300">{t('hero.minutes')}</p>
                    </div>
                    {/* Seconds */}
                    <div className="text-center flex-1">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-white/5 rounded-xl blur-sm"></div>
                        <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 text-white text-lg sm:text-2xl lg:text-3xl font-bold px-2 sm:px-4 py-2 sm:py-3 rounded-xl">
                          {timeLeft.seconds}
                        </div>
                      </div>
                      <p className="text-xs sm:text-sm mt-1 sm:mt-2 text-gray-300">{t('hero.seconds')}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* ------ Buttons ------ */}
              <div className="space-y-4 sm:space-y-6">
                <button
                  onClick={scrollToCheckout}
                  className="group relative w-full lg:w-auto overflow-hidden bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold py-4 sm:py-5 px-6 sm:px-10 rounded-2xl text-base sm:text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-2xl whitespace-nowrap cursor-pointer"
                >
                  <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <div className="relative flex items-center justify-center">
                    <i className="ri-shopping-cart-line mr-2 sm:mr-3 text-lg sm:text-xl"></i>
                    Kupi sada - Samo {selectedModel === 'model1' ? '7990' : productPrice} RSD!
                  </div>
                </button>

                <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm">
                  <div className="flex items-center bg-white/5 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/10">
                    <i className="ri-truck-line text-green-400 mr-1 sm:mr-2"></i>
                    <span className="text-gray-300">Brza dostava za samo 24 do 48h!</span>
                  </div>
                  <div className="flex items-center bg-white/5 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/10">
                    <i className="ri-shield-check-line text-blue-400 mr-1 sm:mr-2"></i>
                    <span className="text-gray-300">{t('hero.guarantee')}</span>
                  </div>
                  <div className="flex items-center bg-white/5 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-full border border-white/10">
                    <i className="ri-star-fill text-yellow-400 mr-1 sm:mr-2"></i>
                    <span className="text-gray-300">{t('hero.rating')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ------ Product Image ------ */}
            <div className="relative animate-fade-in-right order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-blue-500/30 rounded-3xl blur-2xl transform rotate-6"></div>
                <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-4 sm:p-8 transform hover:rotate-1 transition-transform duration-500">
                  <img
                    alt="Smart Parking Barrier with Remote Control"
                    className="w-full h-auto rounded-2xl shadow-2xl object-top"
                    src="hero.png"
                  />

                  {/* Badges */}
                  <div className="absolute -top-2 sm:-top-4 -right-2 sm:-right-4 bg-gradient-to-r from-green-400 to-emerald-5 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-bold shadow-lg animate-bounce">
                    <i className="ri-check-line mr-1 sm:mr-2"></i>
                    {t('hero.inStock')}
                  </div>
                  <div className="absolute -bottom-2 sm:-bottom-4 -left-2 sm:-left-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white px-3 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-bold shadow-lg">
                    <i className="ri-rocket-line mr-1 sm:mr-2"></i>
                    {t('hero.quickInstall')}
                  </div>
                  <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-black/60 backdrop-blur-sm text-white px-3 sm:px-4 py-2 rounded-xl text-xs">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                      <span className="hidden sm:inline">Daljinski upravljač</span>
                      <span className="sm:hidden">Daljinski upravljač</span>
                    </div>
                  </div>
                </div>

                {/* Floating info cards (desktop only) */}
                <div className="hidden lg:block absolute -left-8 top-1/4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 animate-float">
                  <div className="flex items-center text-white">
                    <div className="w-10 h-10 bg-purple-500/20 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-smartphone-line text-purple-400"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-sm">Kontrola daljinskim</p>
                      <p className="text-xs text-gray-300">30m domet</p>
                    </div>
                  </div>
                </div>

                <div className="hidden lg:block absolute -right-8 bottom-1/4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 animate-float-delayed">
                  <div className="flex items-center text-white">
                    <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-battery-charge-line text-blue-400"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{t('hero.battery')}</p>
                      <p className="text-xs text-gray-300">{t('hero.batteryLife')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 text-white animate-bounce">
          <div className="flex flex-col items-center">
            <p className="text-xs sm:text-sm mb-2 text-gray-300">{t('hero.scrollExplore')}</p>
            <i className="ri-arrow-down-line text-xl sm:text-2xl"></i>
          </div>
        </div>
      </section>

      {/* --------------------------- Problem / Solution Section --------------------------- */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* ------ Problem ------ */}
            <div className="space-y-8">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center bg-red-100 text-red-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <i className="ri-error-warning-line mr-2"></i>
                  {t('problem.badge')}
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">Umorni od neovlašćenog parkiranja?</h2>
              </div>

              <div className="space-y-6">
                {/* Stolen Spot */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-car-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('problem.stolenSpot')}</h3>
                    <p className="text-gray-600">{t('problem.stolenSpotDesc')}</p>
                  </div>
                </div>
                {/* Wasting Time */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('problem.wastingTime')}</h3>
                    <p className="text-gray-600">{t('problem.wastingTimeDesc')}</p>
                  </div>
                </div>
                {/* Losing Money */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('problem.losingMoney')}</h3>
                    <p className="text-gray-600">{t('problem.losingMoneyDesc')}</p>
                  </div>
                </div>
                {/* Stress */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-emotion-unhappy-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('problem.stress')}</h3>
                    <p className="text-gray-600">{t('problem.stressDesc')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* ------ Solution ------ */}
            <div className="space-y-8">
              <div className="text-center lg:text-left">
                <div className="inline-flex items-center bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
                  <i className="ri-check-line mr-2"></i>
                  {t('solution.badge')}
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('solution.title')}</h2>
              </div>

              <div className="space-y-6">
                {/* Protected */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('solution.protected')}</h3>
                    <p className="text-gray-600">{t('solution.protectedDesc')}</p>
                  </div>
                </div>

                {/* One Click */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-smartphone-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('solution.oneClick')}</h3>
                    <p className="text-gray-600">Podignite/spustite barijeru daljinskim upravljačem do 30m daleko</p>
                  </div>
                </div>

                {/* No Complications */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-battery-charge-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('solution.noComplications')}</h3>
                    <p className="text-gray-600">{t('solution.noComplicationsDesc')}</p>
                  </div>
                </div>

                {/* Peace */}
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-heart-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('solution.peace')}</h3>
                    <p className="text-gray-600">{t('solution.peaceDesc')}</p>
                  </div>
                </div>

                {/* Result */}
                <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-2xl border border-green-200">
                  <div className="flex items-center mb-3">
                    <i className="ri-lightbulb-line text-green-600 text-2xl mr-3"></i>
                    <h3 className="text-lg font-semibold text-gray-900">{t('solution.result')}</h3>
                  </div>
                  <p className="text-gray-700 font-medium">{t('solution.resultDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- Features Section --------------------------- */}
      <section id="features" className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4 sm:mb-6">
              <i className="ri-star-line mr-2"></i>
              {t('features.badge')}
            </div>
            <h2 className="text-2xl sm:text-3xl lg:4xl font-bold text-gray-900 mb-4 sm:mb-6 px-4">
              Zašto je naša parking barijera najbolja?
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Kombinujemo najnoviju tehnologiju sa praktičnošću da vam damo savršeno rešenje za zaštićeno parking mesto
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: 'ri-smartphone-line',
                title: 'Daljinska kontrola',
                description: 'Kontrolišite barijeru daljinskim upravljačem do 30 metara daleko. Jednostavno korišćenje.',
                gradient: 'from-blue-500 to-cyan-500'
              },
              {
                icon: 'ri-battery-charge-line',
                title: 'Dugotrajna baterija',
                description: 'Baterija traje ~6 meseci sa redovnim korišćenjem. Njena zamena traje svega 30 sekundi!',
                gradient: 'from-green-500 to-emerald-500'
              },
              {
                icon: 'ri-shield-line',
                title: t('features.weatherResistant'),
                description: t('features.weatherResistantDesc'),
                gradient: 'from-orange-500 to-red-500'
              },
              {
                icon: 'ri-tools-line',
                title: t('features.easyInstall'),
                description: t('features.easyInstallDesc'),
                gradient: 'from-purple-500 to-pink-500'
              },
              {
                icon: 'ri-alarm-warning-line',
                title: 'Pametni alarm',
                description: 'Automatski alarm ako neko pokuša da pomeri barijeru bez dozvole.',
                gradient: 'from-yellow-500 to-orange-500'
              },
              {
                icon: 'ri-lock-line',
                title: 'Sigurnosna brava',
                description: 'Automatsko zaključavanje u spuštenoj poziciji. Nemoguće pomeriti bez daljinske kontrole ili fizičkog ključa!',
                gradient: 'from-indigo-500 to-purple-500'
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
              >
                <div className="relative mb-4 sm:mb-6">
                  <div
                    className={`w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}
                  >
                    <i className={`${feature.icon} text-white text-xl sm:text-2xl`}></i>
                  </div>
                  <div
                    className={`absolute inset-0 w-12 sm:w-16 h-12 sm:h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300`}
                  ></div>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 sm:mt-16">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-6 sm:p-8 text-white">
              <h3 className="text-xl sm:text-2xl font-bold mb-4">{t('features.ready')}</h3>
              <p className="text-blue-100 mb-6 text-base sm:text-lg">
                {t('features.readyDesc')}
              </p>
              <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6 text-sm mb-6">
                <div className="flex items-center">
                  <i className="ri-check-line text-green-400 mr-2"></i>
                  <span>Brza dostava za samo 24 do 48h!</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-400 mr-2"></i>
                  <span>{t('features.warranty')}</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-check-line text-green-400 mr-2"></i>
                  <span>{t('features.moneyBack')}</span>
                </div>
              </div>
              <button
                onClick={scrollToCheckout}
                className="bg-white text-blue-600 font-bold py-3 px-8 rounded-xl hover:bg-gray-100 transition-colors whitespace-nowrap text-base sm:text-lg"
              >
                Naručite sada - Samo {selectedModel === 'model1' ? '7990' : '8490'} RSD
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- Testimonials Section --------------------------- */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-green-100 text-green-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-heart-line mr-2"></i>
              {t('testimonials.badge')}
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('testimonials.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Pridružite se hiljadama zadovoljnih korisnika koji su rešili problem parkiranja zauvek
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">10,000+</div>
              <div className="text-gray-600">{t('testimonials.customers')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">4.8/5</div>
              <div className="text-gray-600">{t('testimonials.rating')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">96%</div>
              <div className="text-gray-600">{t('testimonials.recommend')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">8,500+</div>
              <div className="text-gray-600">{t('testimonials.reviews')}</div>
            </div>
          </div>

          {/* Reviews */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm">5.0</span>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "{t('testimonials.mark')}"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-blue-600 font-bold">M</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Marko Petrović</div>
                  <div className="text-gray-600 text-sm">Novi Sad</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm">5.0</span>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "{t('testimonials.anna')}"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-pink-600 font-bold">A</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Ana Jovanović</div>
                  <div className="text-gray-600 text-sm">Beograd</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-400">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <span className="ml-2 text-gray-600 text-sm">5.0</span>
              </div>
              <p className="text-gray-700 mb-4 italic">
                "{t('testimonials.stefan')}"
              </p>
              <div className="flex items-center">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <span className="text-green-600 font-bold">S</span>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Stefan Nikolić</div>
                  <div className="text-gray-600 text-sm">Niš</div>
                </div>
              </div>
            </div>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-award-line text-blue-600 text-2xl"></i>
              </div>
              <div className="font-bold text-gray-900">{t('testimonials.warranty2')}</div>
              <div className="text-gray-600 text-sm">{t('testimonials.warrantyText')}</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-truck-line text-green-600 text-2xl"></i>
              </div>
              <div className="font-bold text-gray-900">{t('testimonials.free')}</div>
              <div className="text-gray-600 text-sm">{t('testimonials.shipping')}</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-customer-service-line text-purple-600 text-2xl"></i>
              </div>
              <div className="font-bold text-gray-900">{t('testimonials.support')}</div>
              <div className="text-gray-600 text-sm">{t('testimonials.supportText')}</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-money-dollar-circle-line text-orange-600 text-2xl"></i>
              </div>
              <div className="font-bold text-gray-900">{t('testimonials.return')}</div>
              <div className="text-gray-600 text-sm">{t('testimonials.returnText')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- Demo Section --------------------------- */}
      <section id="demo" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-purple-100 text-purple-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-eye-line mr-2"></i>
              {t('demo.badge')}
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('demo.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('demo.subtitle')}</p>
          </div>

          <div className="flex justify-center mb-12">
            <div className="bg-white rounded-2xl p-2 shadow-lg">
              <div className="flex space-x-2">
                {['action', 'installation'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeTab === tab
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                    }`}
                  >
                    <i className={`${tab === 'action' ? 'ri-play-circle-line' : 'ri-tools-line'} mr-2`}></i>
                    {tab === 'action' ? t('demo.action') : t('demo.installation')}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              {activeTab === 'action' ? (
                <>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('demo.howItWorks')}</h3>
                    <p className="text-xl text-gray-600 mb-8">{t('demo.simpleControl')}</p>
                  </div>

                  <div className="space-y-4">
                    {['Kontrola sa 30m udaljenosti', t('demo.instant'), t('demo.silent'), t('demo.led')].map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-green-600 text-sm"></i>
                        </div>
                        <span className="text-gray-700 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
                    <div className="flex items-center mb-3">
                      <i className="ri-information-line text-blue-600 text-xl mr-3"></i>
                      <h4 className="font-semibold text-gray-900">{t('demo.additional')}</h4>
                    </div>
                    <p className="text-gray-700">{t('demo.additionalDesc')}</p>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">Jednostavna instalacija</h3>
                    <p className="text-xl text-gray-600 mb-8">
                      Instalirajte barijeru za samo 30 minuta bez profesionalne pomoći
                    </p>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold text-sm">1</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Označite poziciju</h4>
                        <p className="text-gray-600">Odredite gde će biti postavljena barijera i označite mesta za bušenje</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold text-sm">2</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Pričvrstite bazu</h4>
                        <p className="text-gray-600">Izbušite rupe i pričvrstite metalnu bazu pomoću uključenih ankera</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-blue-600 font-bold text-sm">3</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Postavite barijeru</h4>
                        <p className="text-gray-600">Postavite glavnu jedinicu na bazu i zategnite vijke</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-green-600 font-bold text-sm">4</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Testirajte rad</h4>
                        <p className="text-gray-600">Uključite barijeru i testirajte daljinski upravljač</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border border-green-200">
                    <div className="flex items-center mb-3">
                      <i className="ri-tools-line text-green-600 text-xl mr-3"></i>
                      <h4 className="font-semibold text-gray-900">Uključeni alati</h4>
                    </div>
                    <p className="text-gray-700">
                      Svi potrebni alati i materijali su uključeni u paketu. Potrebna vam je samo bušilica.
                    </p>
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  alt={activeTab === 'action' ? 'See How It Works' : 'Installation Steps'}
                  className="w-full h-auto object-cover object-top"
                  src={
                    activeTab === 'action'
                      ? 'https://readdy.ai/api/search-image?query=Smart%20parking%20barrier%20in%20action%20yellow%20black%20modern%20design%20person%20using%20smartphone%20app%20to%20control%20barrier%20in%20parking%20lot%20professional%20photography&width=600&height=400&seq=action1&orientation=landscape'
                      : 'https://readdy.ai/api/search-image?query=Installation%20process%20of%20smart%20parking%20barrier%20step%20by%20step%20tools%20and%20equipment%20professional%20installation%20guide%20modern%20design&width=600&height=400&seq=install1&orientation=landscape'
                  }
                />
                <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                  <button
                    onClick={() => {
                      if (activeTab === 'action') {
                        window.open('https://youtube.com/shorts/DKQslOvipuo?feature=share', '_blank');
                      } else {
                        window.open('https://www.youtube.com/watch?v=d5qDygOOhYA', '_blank');
                      }
                    }}
                    className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors duration-300 group cursor-pointer"
                  >
                    <i className="ri-play-fill text-blue-600 text-3xl ml-1 group-hover:scale-110 transition-transform duration-300"></i>
                  </button>
                </div>
              </div>

              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-check-line text-green-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{t('demo.tested')}</p>
                    <p className="text-xs text-gray-600">{t('demo.cycles')}</p>
                  </div>
                </div>
              </div>

              <div className="absolute -top-6 -right-6 bg-white rounded-2xl p-4 shadow-lg border border-gray-100">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-award-line text-blue-600"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{t('demo.certified')}</p>
                    <p className="text-xs text-gray-600">{t('demo.standards')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- FAQ Section --------------------------- */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-question-line mr-2"></i>
              {t('faq.badge')}
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('faq.title')}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t('faq.subtitle')}</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {[
                { q: t('faq.battery'), a: 'Baterija traje do 6 meseci pri normalnom korišćenju (10-15 podizanja dnevno). Zamena baterije traje samo 30 sekundi!' },
                { q: t('faq.install'), a: t('faq.installAnswer') },
                { q: t('faq.weather'), a: t('faq.weatherAnswer') },
                { q: t('faq.strong'), a: t('faq.strongAnswer') },
                { q: t('faq.multiple'), a: t('faq.multipleAnswer') },
                { q: t('faq.warranty'), a: t('faq.warrantyAnswer') },
                { q: t('faq.return'), a: t('faq.returnAnswer') }
              ].map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl border border-gray-200 overflow-hidden">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer"
                  >
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.q}</h3>
                    <div
                      className={`w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 transition-transform duration-300 flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}
                    >
                      <i className="ri-arrow-down-s-line text-blue-600"></i>
                    </div>
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-6 pb-6">
                      <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-16">
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-8 border border-blue-200">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('faq.stillQuestions')}</h3>
                <p className="text-gray-600 mb-6">{t('faq.support')}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="tel:+381612929053"
                    onClick={trackPhoneClick}
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    061 29 29 053
                  </a>
                  <button
                    onClick={openContactPopup}
                    className="bg-white text-blue-600 border border-blue-600 px-6 py-3 rounded-xl hover:bg-blue-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    {t('faq.sendEmail')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- Order Form Section --------------------------- */}
      <section id="checkout-section" className="py-20 bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-purple-500/5 to-blue-500/5 rounded-full blur-3xl animate-spin-slow"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center bg-gradient-to-r from-yellow-400 to-orange-500 text-black px-6 py-3 rounded-full text-sm font-semibold mb-6 shadow-lg">
                <div className="w-2 h-2 bg-black rounded-full mr-3 animate-pulse"></div>
                <span>Poslednja šansa - Flash Sale!</span>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">
                Naručite Vašu ParkGuard barijeru
              </h2>
              <p className="text-xl text-gray-300">
                Popunite formu i naš tim će Vas kontaktirati u roku od 24h
              </p>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-3xl blur-xl"></div>
              <div className="relative bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl">
                <form
                  id="order-form"
                  action="send-order.php"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  {/* Model Selection */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-3">
                      Izaberite model:
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <label className="relative">
                        <input
                          type="radio"
                          name="model"
                          value="Baterijski - 7990 RSD"
                          checked={selectedModel === 'battery'}
                          onChange={() => selectModel('battery')}
                          className="sr-only"
                        />
                        <div className={`p-4 border-2 rounded-xl cursor-pointer transition-all backdrop-blur-sm ${
                          selectedModel === 'battery' 
                            ? 'border-yellow-400 bg-yellow-400/20 shadow-lg' 
                            : 'border-white/30 bg-white/10 hover:border-white/50 hover:bg-white/20'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-white">Baterijski</h3>
                              <p className="text-sm text-gray-300">Bežični model</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-yellow-400">7990 RSD</p>
                            </div>
                          </div>
                        </div>
                      </label>

                      <label className="relative">
                        <input
                          type="radio"
                          name="model"
                          value="Žični - 8490 RSD"
                          checked={selectedModel === 'wired'}
                          onChange={() => selectModel('wired')}
                          className="sr-only"
                        />
                        <div className={`p-4 border-2 rounded-xl cursor-pointer transition-all backdrop-blur-sm ${
                          selectedModel === 'wired' 
                            ? 'border-yellow-400 bg-yellow-400/20 shadow-lg' 
                            : 'border-white/30 bg-white/10 hover:border-white/50 hover:bg-white/20'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-white">Žični</h3>
                              <p className="text-sm text-gray-300">Sa kablom</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-yellow-400">8490 RSD</p>
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Quantity Selection */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-3">
                      Količina:
                    </label>
                    <div className="flex items-center space-x-4">
                      <button
                        type="button"
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
                      >
                        <i className="ri-subtract-line text-white"></i>
                      </button>
                      <input
                        type="number"
                        name="quantity"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                        min="1"
                        max="10"
                        className="w-20 text-center border border-white/30 bg-white/10 backdrop-blur-sm rounded-lg py-2 text-white placeholder-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setQuantity(Math.min(10, quantity + 1))}
                        className="w-10 h-10 rounded-full border border-white/30 bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors"
                      >
                        <i className="ri-add-line text-white"></i>
                      </button>
                      <span className="text-sm text-gray-300">
                        (maksimalno 10 komada)
                      </span>
                    </div>
                  </div>

                  {/* Bulk Order Option */}
                  <div className="bg-yellow-400/20 border border-yellow-400/30 backdrop-blur-sm rounded-xl p-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-information-line text-yellow-400 mt-0.5"></i>
                      <div>
                        <h4 className="font-medium text-yellow-400">Veće količine</h4>
                        <p className="text-sm text-gray-300 mt-1">
                          Za narudžbine preko 10 komada, kontaktirajte nas direktno na{' '}
                          <a href="tel:+381612929053" onClick={trackPhoneClick} className="font-medium underline text-yellow-400">
                            061 29 29 053
                          </a>{' '}
                          za specijalne cene.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Ime i prezime *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 border border-white/30 bg-white/10 backdrop-blur-sm rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                        placeholder="Unesite vaše ime i prezime"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Email adresa *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border border-white/30 bg-white/10 backdrop-blur-sm rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                        placeholder="vas@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full px-4 py-3 border border-white/30 bg-white/10 backdrop-blur-sm rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                        placeholder="+381 60 123 4567"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Grad *
                      </label>
                      <input
                        type="text"
                        name="city"
                        required
                        className="w-full px-4 py-3 border border-white/30 bg-white/10 backdrop-blur-sm rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm"
                        placeholder="Beograd"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Adresa dostave *
                    </label>
                    <textarea
                      name="address"
                      required
                      rows={3}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-white/30 bg-white/10 backdrop-blur-sm rounded-lg text-white placeholder-gray-300 focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm resize-none"
                      placeholder="Unesite kompletnu adresu za dostavu..."
                    ></textarea>
                  </div>

                  {/* Order Summary */}
                  <div className="bg-black/40 backdrop-blur-sm border border-white/20 rounded-xl p-6">
                    <h3 className="font-semibold text-white mb-4">Rezime narudžbine:</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-300">Model:</span>
                        <span className="font-medium text-white">
                          {selectedModel === 'battery' ? 'Baterijski' : 'Žični'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Cena po komadu:</span>
                        <span className="font-medium text-white">
                          {selectedModel === 'battery' ? '7990' : '8490'} RSD
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Količina:</span>
                        <span className="font-medium text-white">{quantity} kom</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-300">Dostava:</span>
                        <span className="font-medium text-white">390 RSD</span>
                      </div>
                      <div className="border-t border-white/20 pt-2 mt-2">
                        <div className="flex justify-between text-lg font-bold">
                          <span className="text-white">UKUPNO:</span>
                          <span className="text-yellow-400">
                            {((selectedModel === 'battery' ? 7990 : 8490) * quantity + 390).toLocaleString()} RSD
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black py-4 px-8 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Šalje se...
                      </span>
                    ) : (
                      <>
                        <i className="ri-shopping-cart-line mr-2"></i>
                        Naručite sada
                      </>
                    )}
                  </button>

                  {/* Success Message */}
                  {showSuccess && (
                    <div className="bg-green-500/20 border border-green-400/30 backdrop-blur-sm rounded-xl p-4">
                      <div className="flex items-center">
                        <i className="ri-check-circle-line text-green-400 mr-3"></i>
                        <div>
                          <h4 className="font-medium text-green-400">Narudžbina je uspešno poslata!</h4>
                          <p className="text-sm text-gray-300 mt-1">
                            Kontaktiraćemo Vas u roku od 24h radi potvrde narudžbine.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-gray-400 text-center">
                    * Sva polja su obavezna. Vaši podaci su bezbedni i neće biti prosleđeni trećim licima.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --------------------------- Floating CTA --------------------------- */}
      <div className="fixed bottom-4 sm:bottom-6 left-4 right-4 sm:left-6 sm:right-6 z-50 animate-fade-in-up">
        <div className="max-w-sm mx-auto">
          <button
            onClick={scrollToCheckout}
            className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-500 hover:to-orange-600 text-black font-bold py-3 sm:py-4 px-4 sm:px-6 rounded-2xl shadow-2xl transition-all duration-300 transform hover:scale-105 whitespace-nowrap text-sm sm:base cursor-pointer"
          >
            <div className="flex items-center justify-center">
              <i className="ri-shopping-cart-line mr-2 text-base sm:text-lg"></i>
              <span>Naručite sada - Samo {selectedModel === 'model1' ? '7990' : '8490'} RSD!</span>
            </div>
          </button>
        </div>
      </div>

      {/* --------------------------- Footer --------------------------- */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand & Social */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-parking-line text-white text-xl"></i>
                </div>
                <span className="text-xl font-bold">ParkGuard</span>
              </div>

              {/* Company Information */}
              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <h4 className="text-white font-semibold mb-3">Kontakt Informacije</h4>
                <div className="space-y-1 text-sm text-gray-300">
                  <p>Telefon: 061 29 29 053</p>
                  <p>E-mail: prodaja@parkingbarijera.rs</p>
                 
                </div>
              </div>
            </div>
            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.quickLinks')}</h3>
              <ul className="space-y-2">
                <li>
                  <a href="#demo" className="text-gray-400 hover:text-white transition-colors">
                    {t('header.product')}
                  </a>
                </li>
                <li>
                  <a href="#features" className="text-gray-400 hover:text-white transition-colors">
                    {t('header.features')}
                  </a>
                </li>
                <li>
                  <a href="#testimonials" className="text-gray-400 hover:text-white transition-colors">
                    {t('header.reviews')}
                  </a>
                </li>
                <li>
                  <a href="#faq" className="text-gray-400 hover:text-white transition-colors">
                    {t('header.faq')}
                  </a>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{t('footer.support')}</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={openContactPopup} className="text-gray-400 hover:text-white transition-colors">
                    {t('footer.contact')}
                  </button>
                </li>
                <li>
                  <a href="https://www.youtube.com/watch?v=dQw4w9WgXcQ" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                    {t('footer.installation')}
                  </a>
                </li>
                <li>
                  <a href="/garancija" className="text-gray-400 hover:text-white transition-colors">
                    {t('footer.warranty')}
                  </a>
                </li>
                <li>
                  <a href="/povracaj" className="text-gray-400 hover:text-white transition-colors">
                    {t('footer.returns')}
                  </a>
                </li>
                <li>
                  <button onClick={openTechnicalPopup} className="text-gray-400 hover:text-white transition-colors">
                    {t('footer.technicalHelp')}
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">© 2025 ParkGuard. Sva prava zadržana.</p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.terms')}
              </a>
              <a href="#" className="text-gray-400 hover:text-white text-sm transition-colors">
                {t('footer.privacy')}
              </a>
              <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-white text-sm transition-colors">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
